import { UserProfile, UserWorkoutSession } from 'fitbeat.gym-core';
import { KnownMediums } from 'fitbeat.models';
import React, { PureComponent } from 'react';
import { translate } from 'react-i18next';
import { ActivityIndicator, ScrollView, StyleSheet, Text, TouchableOpacity, View } from 'react-native';
import { connect } from 'react-redux';
import CheckMarkCircle from '../../assets/icons/check-mark-circle.svg';
import { Button, ButtonSize, ButtonTypes, Icon } from '../../components';
import { IAppStore } from '../../HomeWorkouts';
import { GymResolver } from '../../services';
import Theme from '../../theme';
import AddToCalendarButtons from '../../ui/components/AddToCalendarButtons';
import { getMomentFormat } from '../../utils';
import { getGymTimezone } from '../../utils/gyms';
import { defaultScale } from '../../utils/scaling';
import { closeDemoBookingSummaryScreen, setupAppPressed as setupAppPressedAction } from '../actions';
import InstallApp from '../components/InstallApp';

interface IProps {
  userProfile: UserProfile;
  workoutSession: UserWorkoutSession | null;
  loadingCloseDemoBookingSummary: boolean;
  medium: KnownMediums;
  closeSummary: (gymName?: string) => void;
  setupAppPressed: () => void;
  setupAppLoading: boolean;
  t: (key: string) => string;
}

class StudioDemoBookingSummaryStep extends PureComponent<IProps> {
  public static getTitle = () => <></>;

  public render() {
    const {
      t, workoutSession, closeSummary, loadingCloseDemoBookingSummary, medium,
      setupAppPressed, setupAppLoading,
    } = this.props;
    const gym = workoutSession && GymResolver.getByName(workoutSession.gymName);
    const summaryShowingOnPTDashboard = medium === KnownMediums.walkin;

    return (
      <ScrollView>
        {summaryShowingOnPTDashboard &&
          <View style={styles.closeContainer}>
            <TouchableOpacity
              onPress={() => closeSummary(workoutSession?.gymName)} disabled={loadingCloseDemoBookingSummary}>
              {loadingCloseDemoBookingSummary &&
                <ActivityIndicator color={Theme.GREY} />
              }
              {!loadingCloseDemoBookingSummary &&
                <Icon
                  size={30}
                  name='md-close'
                  color={Theme.GREY}
                  style={styles.icon}
                />
              }
            </TouchableOpacity>
          </View>
        }
        <View style={styles.topContainer}>
          <CheckMarkCircle height={defaultScale(70)} width={defaultScale(70)} />
          <Text style={styles.heading}>{t('studioWorkoutBooked')}</Text>
          <View style={styles.mainTextContainer}>
            <Text style={styles.content}>
              {getMomentFormat(
                workoutSession?.startTimeOfFirstExercise,
                'HH:mm dddd, Do MMMM',
                getGymTimezone(workoutSession),
              )}
            </Text>
            {gym?.displayAddress &&
              <Text style={styles.content}>{gym.displayAddress}</Text>
            }
          </View>
          {this.renderMessage()}
        </View>
        {!summaryShowingOnPTDashboard &&
          <InstallApp
            header={t('getApp')}
            actionButtons={
              <>
                <Button
                  title={t('continueToAppSetup')}
                  size={ButtonSize.MEDIUM}
                  fontColor={Theme.WHITE}
                  onPress={setupAppPressed}
                  isLoading={setupAppLoading}
                  disabled={setupAppLoading || loadingCloseDemoBookingSummary}
                />
                <Button
                  title={t('setupLater')}
                  size={ButtonSize.MEDIUM} buttonType={ButtonTypes.TRANSPARENT}
                  onPress={() => closeSummary(workoutSession?.gymName)}
                  isLoading={loadingCloseDemoBookingSummary}
                  disabled={setupAppLoading || loadingCloseDemoBookingSummary}
                />
              </>
            }
          />
        }
      </ScrollView>
    );
  }

  private renderMessage() {
    const { workoutSession, medium, t } = this.props;
    if (medium === KnownMediums.walkin) {
      return <Text style={styles.content}>{t('checkInWithInstructor')}</Text>;
    }
    if (workoutSession) {
      return <AddToCalendarButtons workoutSession={workoutSession} t={t} displayHorizontal />;
    }
    return <></>;
  }
}

const styles = StyleSheet.create({
  topContainer: {
    paddingVertical: defaultScale(30),
    alignItems: 'center',
  },
  heading: {
    ...Theme.textStyle.heading,
    marginVertical: defaultScale(20),
  },
  content: {
    ...Theme.textStyle.bodyCopyReg,
    textAlign: 'center',
  },
  icon: {
    lineHeight: 30,
  },
  closeContainer: {
    flexDirection: 'row',
    justifyContent: 'flex-end',
    alignItems: 'center',
  },
  mainTextContainer: {
    marginBottom: defaultScale(20),
  },
});

const mapStateToProps = (state: IAppStore) => ({
  workoutSession: state.bookings.bookingWizard.workoutSession,
  loadingCloseDemoBookingSummary: state.bookings.demo.loadingCloseDemoBookingSummary,
  setupAppLoading: state.bookings.demo.setupAppLoading,
  medium: state.deepLinkParamaters.utm_medium,
});

const mapDispatchToProps = (dispatch, ownProps: IProps) => ({
  closeSummary: (gymName) => dispatch(closeDemoBookingSummaryScreen(ownProps.userProfile, gymName)),
  setupAppPressed: () => dispatch(setupAppPressedAction(ownProps.userProfile)),
});

export default translate(['bookings'], { wait: true })(
  connect(mapStateToProps, mapDispatchToProps)(StudioDemoBookingSummaryStep));
